<script lang="ts">
  import { onMount } from 'svelte';
  import { M2, M3 } from '../modules';
  import type { Station } from '../types';

  export let currentScreen: string = '';

  let currentIdx: number = 1;
  let refreshKey: number = 0;
  let rows: number[] = Array.from({ length: 10 }, (_, i) => i + 1);

  // Create a fresh array reference when refreshKey changes so esbuild-svelte
  // re-evaluates @const blocks in the {#each} — this is the only reliable way
  // to force re-render after M2 mutations.
  $: {
    currentScreen;
    refreshKey;
    rows = Array.from({ length: 10 }, (_, i) => i + 1);
    if (M3) currentIdx = M3.getCurrentIndex?.() ?? 1;
  }

  onMount(() => {
    if (M3) currentIdx = M3.getCurrentIndex?.() ?? 1;
    // Listen for station changes via CustomEvent so the highlight updates
    // when the user navigates via media keys while this screen is visible.
    // M3.onNavigate is single-slot (owned by app.svelte), so we use a
    // separate event channel to avoid overwriting the main callback.
    function onStationChange(e: CustomEvent) {
      currentIdx = e.detail;
      refreshKey++;
    }
    window.addEventListener('stationchange', onStationChange);
    return () => window.removeEventListener('stationchange', onStationChange);
  });

  // Fetch stations fresh from M2 on every call — side-steps all Svelte 4
  // reactivity / closure staleness / esbuild-svelte compilation issues.
  // M2.getStations() is a fast synchronous localStorage read.
  function getStations(): Station[] {
    return M2?.getStations?.() ?? [];
  }

  function getStation(i: number): Station | undefined { return getStations().find((s: Station) => s.index === i); }
  function isLocked(i: number) { return getStation(i)?.locked ?? false; }

  function toggleLock(i: number, e: Event) {
    e.stopPropagation();
    M2?.setLocked?.(i, !isLocked(i));
    refreshKey++;
  }

  function removeStation(i: number, e: Event) {
    e.stopPropagation();
    M2?.removeStation?.(i);
    refreshKey++;
  }

  function onKeyReorder(e: KeyboardEvent, srcIndex: number) {
    if (!e.shiftKey) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const targetIndex = e.key === 'ArrowUp' ? srcIndex - 1 : srcIndex + 1;
    if (targetIndex < 1 || targetIndex > 10) return;
    e.preventDefault();
    M2?.reorderStations?.(srcIndex, targetIndex);
    refreshKey++;
  }
</script>

<div id="station-manager-rows">
  {#each rows as i}
    {@const st = getStation(i)}
    {@const occupied = !!st}
    {@const locked = occupied && st.locked}
    {@const active = currentIdx === i}
    <div
      class="sm-row"
      class:sm-row--active={active}
      class:sm-row--locked={locked}

      tabindex={occupied && !locked ? 0 : -1}
      data-index={i}
      on:keydown={(e) => onKeyReorder(e, i)}>

      <span class="sm-grip">
        <button class="sm-arrow-btn"
          aria-label="Move station {i} up"
          on:click|stopPropagation={() => { if (i > 1) { M2?.reorderStations?.(i, i-1); refreshKey++; } }}>▲</button>
        <button class="sm-arrow-btn"
          aria-label="Move station {i} down"
          on:click|stopPropagation={() => { if (i < 10) { M2?.reorderStations?.(i, i+1); refreshKey++; } }}>▼</button>
      </span>

      <span class="sm-num">{i}.</span>

      <span class="sm-name" class:empty={!occupied}>
        {occupied ? st.label : '(empty)'}
      </span>

      <button class="sm-lock" class:locked title={locked ? 'Unlock' : 'Lock'}
        aria-label="{locked ? 'Unlock' : 'Lock'} station {i}"
        on:click={(e) => toggleLock(i, e)}>
        {locked ? '★' : '☆'}
      </button>

      <span class="sm-remove-area">
        {#if occupied && !locked}
          <button class="sm-remove" title="Remove"
            aria-label="Remove station {i}"
            on:click={(e) => removeStation(i, e)}>[X]</button>
        {/if}
      </span>
    </div>
  {/each}
</div>
